import { Link } from "react-router";
import { Icon } from "../components/icons";
import { Badge, BtnInner, Empty, ErrorNote, Loading, PageHead } from "../components/kit";
import type { Client } from "../lib/api";
import { ago } from "../lib/format";
import { useResource } from "../lib/hooks";

export function ClientBadges({ client }: { client: Client }) {
  return (
    <span className="badges">
      <Badge tone={client.kind === "customer" ? "accent" : "muted"}>{client.kind === "customer" ? "Customer site" : "Group site"}</Badge>
      {client.disabled && <Badge tone="danger">Disabled</Badge>}
      {client.access === "listed" ? <Badge tone="warn">Listed accounts only</Badge> : <Badge>Every account</Badge>}
      <Badge>{client.public ? "Public · PKCE" : "Confidential"}</Badge>
      {client.managed && <Badge tone="ok">This panel</Badge>}
    </span>
  );
}

export function ClientsPage() {
  const { data, error } = useResource<{ clients: Client[]; issuer: string }>("/clients");
  return (
    <>
      <PageHead
        title="Portals"
        lede={`Sites that sign people in through Mashriqain ID${data ? ` (${data.issuer})` : ""}. A customer's site can be limited to the accounts you list for it.`}
      >
        <Link to="/portals/new" className="btn" data-variant="primary">
          <Icon name="plus" size={16} />
          <BtnInner>Register a portal</BtnInner>
        </Link>
      </PageHead>
      <ErrorNote error={error} />
      <section className="table-card">
        <div className="table-wrap">
          {!data ? (
            <Loading />
          ) : data.clients.length === 0 ? (
            <Empty title="No portals yet.">
              <Link to="/portals/new" className="link">
                Register the first one
              </Link>
            </Empty>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Portal</th>
                  <th>Type</th>
                  <th>Returns to</th>
                  <th>Changed</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {data.clients.map((c) => (
                  <tr key={c.id} data-disabled={c.disabled || undefined}>
                    <td>
                      <Link to={`/portals/${c.id}`} className="row-person">
                        <span className="tile" data-kind={c.kind}>
                          <Icon name={c.kind === "customer" ? "portals" : "shield"} size={16} />
                        </span>
                        <span className="row-link">
                          <span className="row-title">{c.name}</span>
                          <span className="row-sub code">{c.id}</span>
                        </span>
                      </Link>
                    </td>
                    <td>
                      <ClientBadges client={c} />
                    </td>
                    <td className="muted code small" data-label="Returns to">{c.redirect_uris.map((u) => hostOf(u)).join(", ")}</td>
                    <td className="muted hide-phone">{ago(c.updated_at)}</td>
                    <td className="row-go">
                      <Link to={`/portals/${c.id}`} className="go" aria-label={`Open ${c.name}`}>
                        →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}

function hostOf(uri: string) {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}
